import { FOLIATION_OR_PAGE_PATTERNS, STANDALONE_NUMERIC_LINE } from './comparisonTextCleaner'
import {
  ARABIC_TRAILING_FOOTNOTE_ANCHOR_REGEX_SOURCE,
  FOOTNOTE_BODY_LINE_REGEX_SOURCE,
} from './editorialMarkerGrammar'

export const SOURCE_TEXT_CLEANER_VERSION = '2026-04-28.v1'
export const SOURCE_TEXT_CLEANING_POLICY_ID = 'normalized_arabic_transcription.v1'

export type SourceCleaningArtifactAction = 'extract' | 'remove' | 'preserve'

export interface SourceCleaningArtifact {
  type: string
  action: SourceCleaningArtifactAction
  text: string
  start: number
  end: number
}

export interface SourceCleaningReport {
  policy_id: string
  cleaner_version: string
  counts: Record<string, number>
  artifacts: SourceCleaningArtifact[]
  warnings: string[]
  removed_char_count: number
  clean_text_length: number
}

export interface SourceCleaningResult {
  cleanSourceText: string
  report: SourceCleaningReport
}

const ZERO_WIDTH_OR_BOM = /[\u200B-\u200D\u2060\uFEFF]/g
const BIDI_CONTROL = /[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g
const NON_BREAKING_SPACE = /\u00A0/g
const INLINE_BRACKETED_FOLIATION = /\[(?:\*?\d+\s*[rv]\*?|\*\d+\s*[rv]|\d+\s*[rv]\*?)\]/gi
const PAGE_OR_PDF_LINE = /^\s*(?:p(?:age)?\.?\s*)?[0-9\u0660-\u0669\u06F0-\u06F9]+\s*$|^\s*pdf\s*[0-9\u0660-\u0669\u06F0-\u06F9]+\s*$/gim
const FOOTNOTE_BODY_LINE = new RegExp(FOOTNOTE_BODY_LINE_REGEX_SOURCE, 'gim')
const LINE_NUMBER_PREFIX = /^\s*[0-9\u0660-\u0669\u06F0-\u06F9]+[\]).:-]?[ \t]+(?!(?:apparatus|note)\b)(?=\S)/gim
const ARABIC_TRAILING_FOOTNOTE_ANCHOR = new RegExp(ARABIC_TRAILING_FOOTNOTE_ANCHOR_REGEX_SOURCE, 'g')
const HORIZONTAL_SPACE = /[ \t]+/g

const normalizeLineEndings = (value: string): string => value.replace(/\r\n?/g, '\n')

const cloneGlobal = (pattern: RegExp): RegExp => (
  new RegExp(pattern.source, pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`)
)

const collectArtifacts = (
  text: string,
  pattern: RegExp,
  type: string,
  action: SourceCleaningArtifactAction,
): SourceCleaningArtifact[] => {
  const matcher = cloneGlobal(pattern)
  const artifacts: SourceCleaningArtifact[] = []
  let match: RegExpExecArray | null = matcher.exec(text)
  while (match) {
    const matchedText = match[0] || ''
    if (matchedText) {
      artifacts.push({
        type,
        action,
        text: matchedText,
        start: match.index,
        end: match.index + matchedText.length
      })
    }
    match = matcher.exec(text)
  }
  return artifacts
}

const countByType = (artifacts: SourceCleaningArtifact[]): Record<string, number> => (
  artifacts.reduce<Record<string, number>>((counts, artifact) => {
    counts[artifact.type] = (counts[artifact.type] || 0) + 1
    return counts
  }, {})
)

const rangesOverlap = (
  first: Pick<SourceCleaningArtifact, 'start' | 'end'>,
  second: Pick<SourceCleaningArtifact, 'start' | 'end'>
): boolean => first.start < second.end && second.start < first.end

const collectNonOverlappingFoliationArtifacts = (text: string): SourceCleaningArtifact[] => {
  const artifacts: SourceCleaningArtifact[] = []
  for (const pattern of [INLINE_BRACKETED_FOLIATION, ...FOLIATION_OR_PAGE_PATTERNS]) {
    for (const artifact of collectArtifacts(text, pattern, 'foliation', 'extract')) {
      if (artifacts.some(existing => rangesOverlap(existing, artifact))) {
        continue
      }
      artifacts.push(artifact)
    }
  }
  return artifacts
}

export function cleanSourceTextForModel(text: string): SourceCleaningResult {
  const normalized = normalizeLineEndings(text || '').replace(NON_BREAKING_SPACE, ' ')
  const artifacts: SourceCleaningArtifact[] = [
    ...collectArtifacts(normalized, ZERO_WIDTH_OR_BOM, 'invisible_control', 'remove'),
    ...collectArtifacts(normalized, BIDI_CONTROL, 'bidi_control', 'remove'),
    ...collectNonOverlappingFoliationArtifacts(normalized),
    ...collectArtifacts(normalized, PAGE_OR_PDF_LINE, 'page_number_line', 'extract'),
    ...collectArtifacts(normalized, STANDALONE_NUMERIC_LINE, 'standalone_numeric_line', 'extract'),
    ...collectArtifacts(normalized, FOOTNOTE_BODY_LINE, 'footnote_body_line', 'extract'),
    ...collectArtifacts(normalized, LINE_NUMBER_PREFIX, 'line_number_prefix', 'extract'),
    ...collectArtifacts(normalized, ARABIC_TRAILING_FOOTNOTE_ANCHOR, 'footnote_anchor', 'extract')
  ]

  let cleaned = normalized
  cleaned = cleaned.replace(ZERO_WIDTH_OR_BOM, '')
  cleaned = cleaned.replace(BIDI_CONTROL, '')
  cleaned = cleaned.replace(INLINE_BRACKETED_FOLIATION, ' ')
  for (const pattern of FOLIATION_OR_PAGE_PATTERNS) {
    cleaned = cleaned.replace(pattern, ' ')
  }
  cleaned = cleaned.replace(PAGE_OR_PDF_LINE, '\n')
  cleaned = cleaned.replace(STANDALONE_NUMERIC_LINE, '\n')
  cleaned = cleaned.replace(FOOTNOTE_BODY_LINE, '\n')
  cleaned = cleaned.replace(LINE_NUMBER_PREFIX, '')
  cleaned = cleaned.replace(ARABIC_TRAILING_FOOTNOTE_ANCHOR, '$1')
  cleaned = cleaned
    .split('\n')
    .map(line => line.replace(HORIZONTAL_SPACE, ' ').trim())
    .filter(Boolean)
    .join('\n')
    .replace(/\n{2,}/g, '\n')
    .trim()

  const removedCharCount = Math.max(0, normalized.length - cleaned.length)
  return {
    cleanSourceText: cleaned,
    report: {
      policy_id: SOURCE_TEXT_CLEANING_POLICY_ID,
      cleaner_version: SOURCE_TEXT_CLEANER_VERSION,
      counts: countByType(artifacts),
      artifacts,
      warnings: [],
      removed_char_count: removedCharCount,
      clean_text_length: cleaned.length
    }
  }
}
